/**
 * Task 151-B — the six per-service-type config sections (Social / Ads /
 * Influencer / Production / Content / Brand), mapped from the reference
 * packages drawer onto V2 tokens + the 151-A `@kmi/contracts` typed
 * shapes. Each section is controlled: it renders the typed config and
 * emits the next typed config. The Arabic enum labels live here (the
 * contract owns the keys only).
 */

import {
  AD_PLATFORM_KEYS,
  ADS_PRICING_MODES,
  PRODUCTION_TYPE_KEYS,
  SOCIAL_PLATFORM_KEYS,
  type AdsServiceConfig,
  type BrandServiceConfig,
  type ContentServiceConfig,
  type InfluencerServiceConfig,
  type ProductionServiceConfig,
  type SocialServiceConfig,
} from '@kmi/contracts';
import { useState, type CSSProperties } from 'react';
import { SimpleField } from '../formPrimitives';
import {
  AD_PLATFORM_LABELS,
  ADS_PRICING_LABELS,
  PRODUCTION_TYPE_LABELS,
  SOCIAL_PLATFORM_LABELS,
} from './labels';
import { ChipGrid, NumberField, SectionTitle, ToggleRow } from './primitives';
import { autoWeeklyQuota, quotaIsZero, socialWeeklyHint } from '../socialWeeklyHint';

const socialPlatformItems = SOCIAL_PLATFORM_KEYS.map((k) => ({
  key: k,
  label: SOCIAL_PLATFORM_LABELS[k],
}));
const adPlatformItems = AD_PLATFORM_KEYS.map((k) => ({ key: k, label: AD_PLATFORM_LABELS[k] }));
const productionTypeItems = PRODUCTION_TYPE_KEYS.map((k) => ({
  key: k,
  label: PRODUCTION_TYPE_LABELS[k],
}));

function toggleKey<T extends string>(arr: ReadonlyArray<T>, k: T): T[] {
  return arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k];
}

const numericGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: 8,
};

// ── Social ───────────────────────────────────────────────────────────

export function SocialSection({
  cfg,
  onChange,
  disabled,
}: {
  readonly cfg: SocialServiceConfig;
  readonly onChange: (next: SocialServiceConfig) => void;
  readonly disabled?: boolean;
}) {
  // Task 280C — derived weekly publishing hint from the monthly counts.
  const hint = socialWeeklyHint(cfg.monthly);
  // Task 280D — auto-calculate weekly (default) vs manual override. The mode is
  // signalled by the weekly quota itself: all-zero = auto, non-zero = manual.
  const [autoWeekly, setAutoWeekly] = useState(() => quotaIsZero(cfg.weekly));
  return (
    <>
      <SectionTitle>إعدادات إدارة السوشال ميديا</SectionTitle>
      <div className="auth-field" style={{ marginBottom: 0 }}>
        <label className="auth-field-label">المنصّات المُدارة</label>
        <ChipGrid
          items={socialPlatformItems}
          selected={cfg.platforms}
          disabled={disabled}
          onToggle={(k) => onChange({ ...cfg, platforms: toggleKey(cfg.platforms, k) })}
        />
      </div>
      <div className="auth-field" style={{ marginBottom: 0 }}>
        <label className="auth-field-label">المحتوى الشهري</label>
        <div style={numericGrid}>
          <NumberField
            label="تصاميم"
            value={cfg.monthly.designs}
            disabled={disabled}
            onChange={(v) => onChange({ ...cfg, monthly: { ...cfg.monthly, designs: v } })}
          />
          <NumberField
            label="فيديوهات"
            value={cfg.monthly.videos}
            disabled={disabled}
            onChange={(v) => onChange({ ...cfg, monthly: { ...cfg.monthly, videos: v } })}
          />
          <NumberField
            label="ستوريات"
            value={cfg.monthly.stories}
            disabled={disabled}
            onChange={(v) => onChange({ ...cfg, monthly: { ...cfg.monthly, stories: v } })}
          />
        </div>
        <p className="tiny" style={{ margin: '6px 0 0', color: 'var(--muted)' }}>
          الافتراض الأسبوعي المشتق من الشهري (تقريبي): ≈ {hint.feedSlotsPerWeek} منشور/تصميم
          بالأسبوع · ≈ {hint.reelsVideosSlotsPerWeek} فيديو/ريلز بالأسبوع · ستوري{' '}
          {hint.storiesMonthlyVolume}/شهر (حجم فقط — لا يُجدول كخانة نشر).
        </p>
      </div>
      <ToggleRow
        label="نحتسب النشر الأسبوعي تلقائيًا"
        desc="نعرض التوزيع الأسبوعي المشتق من الشهري. أوقفه لإدخال قيم أسبوعية يدوية."
        value={autoWeekly}
        disabled={disabled}
        onChange={(on) => {
          setAutoWeekly(on);
          // Auto: zero the weekly quota (the all-zero signal persists the mode
          // and reload re-infers auto). Manual: pre-fill with the derived
          // approximation as an editable starting point (stories stay 0).
          onChange({
            ...cfg,
            weekly: on ? { designs: 0, videos: 0, stories: 0 } : autoWeeklyQuota(cfg.monthly),
          });
        }}
      />
      {!autoWeekly ? (
        <div className="auth-field" style={{ marginBottom: 0 }}>
          <label className="auth-field-label">النشر الأسبوعي (تجاوز يدوي)</label>
          <div style={numericGrid}>
            <NumberField
              label="تصاميم / أسبوع"
              value={cfg.weekly.designs}
              step={0.5}
              disabled={disabled}
              onChange={(v) => onChange({ ...cfg, weekly: { ...cfg.weekly, designs: v } })}
            />
            <NumberField
              label="فيديوهات / أسبوع"
              value={cfg.weekly.videos}
              step={0.5}
              disabled={disabled}
              onChange={(v) => onChange({ ...cfg, weekly: { ...cfg.weekly, videos: v } })}
            />
            <NumberField
              label="ستوريات / أسبوع"
              value={cfg.weekly.stories}
              disabled={disabled}
              onChange={(v) => onChange({ ...cfg, weekly: { ...cfg.weekly, stories: v } })}
            />
          </div>
        </div>
      ) : null}
      <div className="auth-field" style={{ marginBottom: 0 }}>
        <label className="auth-field-label">تخصيص المحتوى حسب المنصة</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['unified', 'custom'] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`chip${cfg.strategy === s ? ' on' : ''}`}
              aria-pressed={cfg.strategy === s}
              disabled={disabled}
              onClick={() => onChange({ ...cfg, strategy: s })}
            >
              {s === 'unified' ? 'محتوى واحد لجميع المنصات' : 'محتوى مخصّص لكل منصّة'}
            </button>
          ))}
        </div>
      </div>
      <ToggleRow
        label="الرد على العملاء"
        desc="الرد على التعليقات والرسائل على السوشال ميديا"
        value={cfg.reply}
        disabled={disabled}
        onChange={(v) => onChange({ ...cfg, reply: v })}
      />
    </>
  );
}

// ── Ads ──────────────────────────────────────────────────────────────

export function AdsSection({
  cfg,
  onChange,
  disabled,
}: {
  readonly cfg: AdsServiceConfig;
  readonly onChange: (next: AdsServiceConfig) => void;
  readonly disabled?: boolean;
}) {
  const showAmount = cfg.pricing === 'fixed' || cfg.pricing === 'hybrid';
  const showPercentage = cfg.pricing === 'percentage' || cfg.pricing === 'hybrid';
  return (
    <>
      <SectionTitle>إعدادات الحملات الإعلانية</SectionTitle>
      <div className="auth-field" style={{ marginBottom: 0 }}>
        <label className="auth-field-label">نوع التسعير</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ADS_PRICING_MODES.map((mode) => (
            <button
              key={mode}
              type="button"
              className={`chip${cfg.pricing === mode ? ' on' : ''}`}
              aria-pressed={cfg.pricing === mode}
              disabled={disabled}
              onClick={() => onChange({ ...cfg, pricing: mode })}
            >
              {ADS_PRICING_LABELS[mode]}
            </button>
          ))}
        </div>
      </div>
      {showAmount ? (
        <NumberField
          label="المبلغ الثابت (ر.س)"
          value={cfg.amount ?? 0}
          disabled={disabled}
          onChange={(v) => onChange({ ...cfg, amount: v })}
        />
      ) : null}
      {showPercentage ? (
        <NumberField
          label="النسبة (%)"
          value={cfg.percentage ?? 0}
          step={0.5}
          disabled={disabled}
          onChange={(v) => onChange({ ...cfg, percentage: v })}
        />
      ) : null}
      <SimpleField
        label="تفاصيل إضافية"
        value={cfg.details}
        textarea
        maxLength={500}
        placeholder="تفاصيل الخدمة…"
        onChange={(v) => onChange({ ...cfg, details: v })}
      />
      <div className="auth-field" style={{ marginBottom: 0 }}>
        <label className="auth-field-label">منصات الإدارة</label>
        <ChipGrid
          items={adPlatformItems}
          selected={cfg.platforms}
          disabled={disabled}
          onToggle={(k) => onChange({ ...cfg, platforms: toggleKey(cfg.platforms, k) })}
        />
      </div>
    </>
  );
}

// ── Influencer ───────────────────────────────────────────────────────

export function InfluencerSection({
  cfg,
  onChange,
  disabled,
}: {
  readonly cfg: InfluencerServiceConfig;
  readonly onChange: (next: InfluencerServiceConfig) => void;
  readonly disabled?: boolean;
}) {
  return (
    <>
      <SectionTitle>إعدادات تسويق المؤثرين</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <NumberField
          label="عدد المؤثرين / شهر"
          value={cfg.count}
          disabled={disabled}
          onChange={(v) => onChange({ ...cfg, count: v })}
        />
        <SimpleField
          label="الفئة"
          value={cfg.category}
          maxLength={120}
          placeholder="مثال: متوسط (10K–100K)"
          onChange={(v) => onChange({ ...cfg, category: v })}
        />
      </div>
      <SimpleField
        label="تفاصيل إضافية"
        value={cfg.details}
        textarea
        maxLength={500}
        placeholder="مثل: تركيز على مؤثري الرياض، أو فئة عمرية محددة…"
        onChange={(v) => onChange({ ...cfg, details: v })}
      />
    </>
  );
}

// ── Production ───────────────────────────────────────────────────────

export function ProductionSection({
  cfg,
  onChange,
  disabled,
}: {
  readonly cfg: ProductionServiceConfig;
  readonly onChange: (next: ProductionServiceConfig) => void;
  readonly disabled?: boolean;
}) {
  return (
    <>
      <SectionTitle>إعدادات إنتاج الفوتو والفيديو</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <NumberField
          label="عدد الصور"
          value={cfg.photos}
          disabled={disabled}
          onChange={(v) => onChange({ ...cfg, photos: v })}
        />
        <NumberField
          label="عدد الفيديوهات"
          value={cfg.videos}
          disabled={disabled}
          onChange={(v) => onChange({ ...cfg, videos: v })}
        />
      </div>
      <div className="auth-field" style={{ marginBottom: 0 }}>
        <label className="auth-field-label">نوع الإنتاج</label>
        <ChipGrid
          items={productionTypeItems}
          selected={cfg.types}
          disabled={disabled}
          onToggle={(k) => onChange({ ...cfg, types: toggleKey(cfg.types, k) })}
        />
      </div>
    </>
  );
}

// ── Content ──────────────────────────────────────────────────────────

export function ContentSection({
  cfg,
  onChange,
  disabled,
}: {
  readonly cfg: ContentServiceConfig;
  readonly onChange: (next: ContentServiceConfig) => void;
  readonly disabled?: boolean;
}) {
  return (
    <>
      <SectionTitle>إعدادات صناعة المحتوى</SectionTitle>
      <NumberField
        label="عدد المقالات / المنشورات الشهرية"
        value={cfg.monthly_count}
        disabled={disabled}
        onChange={(v) => onChange({ ...cfg, monthly_count: v })}
      />
      <ToggleRow
        label="كتابة كابشن للمنشورات"
        desc="نصوص كابشن جاهزة لجميع منشورات السوشال"
        value={cfg.caption}
        disabled={disabled}
        onChange={(v) => onChange({ ...cfg, caption: v })}
      />
      <ToggleRow
        label="استراتيجية محتوى شهرية"
        desc="خطة محتوى مكتوبة قبل بداية الشهر"
        value={cfg.strategy}
        disabled={disabled}
        onChange={(v) => onChange({ ...cfg, strategy: v })}
      />
      <ToggleRow
        label="مقالات SEO"
        desc="مقالات للموقع الإلكتروني محسّنة لمحركات البحث"
        value={cfg.seo}
        disabled={disabled}
        onChange={(v) => onChange({ ...cfg, seo: v })}
      />
    </>
  );
}

// ── Brand ────────────────────────────────────────────────────────────

export function BrandSection({
  cfg,
  onChange,
  disabled,
}: {
  readonly cfg: BrandServiceConfig;
  readonly onChange: (next: BrandServiceConfig) => void;
  readonly disabled?: boolean;
}) {
  const [customExtra, setCustomExtra] = useState('');
  const addExtra = () => {
    const t = customExtra.trim();
    if (t.length === 0 || cfg.extras.includes(t)) {
      setCustomExtra('');
      return;
    }
    onChange({ ...cfg, extras: [...cfg.extras, t] });
    setCustomExtra('');
  };
  return (
    <>
      <SectionTitle>إعدادات الهوية البصرية</SectionTitle>
      <ToggleRow
        label="الشعار (Logo)"
        desc="تصميم الشعار بصيغه المختلفة"
        value={cfg.logo}
        disabled={disabled}
        onChange={(v) => onChange({ ...cfg, logo: v })}
      />
      <ToggleRow
        label="الهوية البصرية"
        desc="الألوان، الخطوط، نظام التصميم"
        value={cfg.identity}
        disabled={disabled}
        onChange={(v) => onChange({ ...cfg, identity: v })}
      />
      <ToggleRow
        label="ملف البراند (Brand Guidelines)"
        desc="دليل استخدام شامل بصيغة PDF"
        value={cfg.guidelines}
        disabled={disabled}
        onChange={(v) => onChange({ ...cfg, guidelines: v })}
      />
      <div className="auth-field" style={{ marginBottom: 0 }}>
        <label className="auth-field-label">عناصر إضافية (أكواب، أقلام، بطاقات…)</label>
        {cfg.extras.length > 0 ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {cfg.extras.map((it) => (
              <button
                key={it}
                type="button"
                className="chip on"
                disabled={disabled}
                title="إزالة"
                onClick={() => onChange({ ...cfg, extras: cfg.extras.filter((x) => x !== it) })}
              >
                {it} ×
              </button>
            ))}
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            className="auth-input"
            value={customExtra}
            disabled={disabled}
            placeholder="عنصر مخصّص…"
            maxLength={80}
            onChange={(e) => setCustomExtra(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addExtra();
              }
            }}
            style={{ paddingInlineStart: 18, flex: 1 }}
          />
          <button
            type="button"
            className="chip"
            onClick={addExtra}
            disabled={disabled || customExtra.trim().length === 0}
          >
            إضافة
          </button>
        </div>
      </div>
      <SimpleField
        label="ملاحظات إضافية"
        value={cfg.notes}
        textarea
        maxLength={500}
        placeholder="أي تفاصيل إضافية تود إضافتها…"
        onChange={(v) => onChange({ ...cfg, notes: v })}
      />
    </>
  );
}
